import { useEffect, useState } from "react";
import MDEditor, { commands, getCommands } from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { THEME_EVENT, getResolvedTheme } from "../utility/theme";

/* ============================================================================
   Markdown editor.

   @uiw/react-md-editor: a textarea with a GFM toolbar (headings, bold, lists,
   task lists, tables, code, quotes, links) and write / live / preview modes.
   No images: the toolbar's image button is dropped.

   The preview runs rehype-sanitize, the same pass <MarkdownView/> uses, so
   what the admin previews is exactly what employees will see.

   The library picks its palette from data-color-mode, falling back to the
   OS's prefers-color-scheme. That is not the app's mode when the user has
   pinned one in the appearance control (dark text on a dark OS over our
   white card), so the wrapper always says which mode is on screen.
   ========================================================================== */

const toolbar = getCommands().filter(
  (command) => command.name !== commands.image.name,
);

/* preview: "edit" | "live" | "preview" — the toolbar's right-hand buttons
   switch between them; "edit" suits a half-width card.
   height: a number fixes it; "auto" grows with the text from a 320px floor
   (theme.css, .md-field-grow) and the page scrolls instead of the editor. */
const MarkdownEditor = ({
  value,
  onChange,
  height = 360,
  preview = "edit",
  invalid = false,
}) => {
  const [colorMode, setColorMode] = useState(getResolvedTheme);
  useEffect(() => {
    const onThemeChange = (e) => setColorMode(e.detail.resolved);
    window.addEventListener(THEME_EVENT, onThemeChange);
    return () => window.removeEventListener(THEME_EVENT, onThemeChange);
  }, []);

  return (
    <div
      className={
        "md-field" +
        (height === "auto" ? " md-field-grow" : "") +
        (invalid ? " is-invalid" : "")
      }
      data-color-mode={colorMode}
    >
      <MDEditor
        value={value}
        onChange={(next) => onChange(next ?? "")}
        height={height}
        commands={toolbar}
        preview={preview}
        visibleDragbar={false}
        previewOptions={{
          className: "typeset",
          rehypePlugins: [[rehypeSanitize]],
        }}
        textareaProps={{ placeholder: "Write in Markdown." }}
      />
    </div>
  );
};

export default MarkdownEditor;
